import { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Headset, Send, Bot, Globe, MessageCircle } from "lucide-react";

const ROLE_LABEL = { user: "Cliente", assistant: "Assistente IA", human: "Você" };
const NOTIFY_LABEL = {
  enviado: "Profissional avisada no WhatsApp",
  nao_configurado: "Aviso por WhatsApp não configurado — visível só no painel",
  nao_necessario: "Profissional já respondeu pelo app WhatsApp",
};

const HandoffCard = ({ h, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = h.session_id;

  const reply = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await adminApi.post(`/handoffs/${encodeURIComponent(sid)}/reply`, { text });
      setText("");
      toast.success("Resposta enviada");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    await adminApi.post(`/handoffs/${encodeURIComponent(sid)}/release`);
    toast.success("Conversa devolvida para a IA");
    onDone();
  };

  const ChannelIcon = h.channel === "whatsapp" ? MessageCircle : Globe;
  return (
    <div data-testid={`handoff-card-${sid}`} className="p-5 border-b border-[#222] last:border-b-0">
      <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
        <ChannelIcon size={14} className="text-[#D4AF37]" />
        <span className="text-white/90">{h.contact_name || h.phone || `Site #${sid.slice(0, 8)}`}</span>
        <span className="text-white/40">•</span>
        <span data-testid={`handoff-reason-${sid}`} className="text-amber-400">Motivo: {h.handoff_reason}</span>
        <span className="text-white/40">•</span>
        <span className="text-white/50">{NOTIFY_LABEL[h.handoff_notified] || h.handoff_notified}</span>
      </div>
      <div data-testid={`handoff-history-${sid}`} className="max-h-56 overflow-y-auto space-y-1.5 bg-[#050505] border border-[#1a1a1a] p-3 mb-3">
        {h.messages.map((m, i) => (
          <div key={i} className="text-xs">
            <span className={m.role === "user" ? "text-[#D4AF37]" : "text-white/40"}>{ROLE_LABEL[m.role] || m.role}: </span>
            <span className="text-white/80 whitespace-pre-wrap">{m.content}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`handoff-reply-input-${sid}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && reply()}
          placeholder={h.channel === "whatsapp" ? "Responder pelo WhatsApp..." : "Responder no chat do site..."}
          className="flex-1 bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-white text-sm px-3 py-2"
        />
        <button data-testid={`handoff-reply-btn-${sid}`} onClick={reply} disabled={busy || !text.trim()} className="btn-secondary-outline text-xs disabled:opacity-40">
          <Send size={14} /> Enviar
        </button>
        <button data-testid={`handoff-release-btn-${sid}`} onClick={release} className="btn-secondary-outline text-xs">
          <Bot size={14} /> Devolver para IA
        </button>
      </div>
    </div>
  );
};

export const HandoffPanel = () => {
  const [items, setItems] = useState([]);
  const [wa, setWa] = useState(null);

  const load = useCallback(async () => {
    try {
      const [h, w] = await Promise.all([adminApi.get(`/handoffs`), adminApi.get(`/whatsapp/status`)]);
      setItems(h.data);
      setWa(w.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <Headset size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
        <span className="overline">Atendimento humano</span>
        {items.length > 0 && (
          <span data-testid="handoff-count-badge" className="ml-2 text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 border border-amber-500/40">
            {items.length} aguardando
          </span>
        )}
      </div>
      <section data-testid="admin-handoffs" className="bg-[#0A0A0A] border border-[#222] mb-12">
        <div className="px-6 py-3 border-b border-[#222] text-xs text-white/50" data-testid="whatsapp-status">
          WhatsApp Cloud API:{" "}
          {wa?.configured ? <span className="text-emerald-400">conectada</span> : <span className="text-amber-400">não configurada (aguardando credenciais da Meta)</span>}
        </div>
        {items.length === 0 ? (
          <div data-testid="handoff-empty" className="p-8 text-center text-white/50 text-sm">Nenhuma conversa aguardando atendimento humano.</div>
        ) : (
          items.map((h) => <HandoffCard key={h.session_id} h={h} onDone={load} />)
        )}
      </section>
    </>
  );
};
